import React from 'react';
import { PencilIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { Expense } from '../../types/expense';
import { isTempId } from '../../utils/expenseUtils';

interface ExpenseCardProps {
  expense: Expense;
  onEdit: (expense: Expense) => void;
  onSync?: (expense: Expense) => void;
  isSyncing?: boolean;
  isUpdating?: boolean; // New prop for update indicator
}

const ExpenseCard: React.FC<ExpenseCardProps> = ({ expense, onEdit, onSync, isSyncing = false, isUpdating = false }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isTemp = isTempId(expense.expense_id);
  const needsSync = isTemp || !expense.synced;
  const showSynced = !isTemp && expense.synced;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all">
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                {expense.article}
              </h3>
              {isUpdating && (
                <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full animate-pulse flex-shrink-0">
                  Обновление...
                </span>
              )}
              {needsSync && !isUpdating && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full flex-shrink-0 animate-pulse">
                  Синхронизируем...
                </span>
              )}
              {showSynced && !isUpdating && (
                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full flex-shrink-0">
                  Синхронизирован
                </span>
              )}
            </div>
            <span className="text-xl font-bold text-green-600 ml-3 flex-shrink-0" style={{fontFamily: "'Times New Roman', Times, serif"}}>
              {formatAmount(expense.amount)} ₽
            </span>
          </div>
          
          {expense.comment && (
            <p className="text-gray-700 text-sm mb-3 leading-relaxed" style={{fontFamily: "'Times New Roman', Times, serif"}}>
              {expense.comment}
            </p>
          )}
          
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{fontFamily: "'Times New Roman', Times, serif"}}>{expense.restaurant_name}</span>
              {expense.sheet_row && (
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                  Строка {expense.sheet_row}
                </span>
              )}
            </div>
            <span className="flex-shrink-0 ml-2">{formatDate(expense.expense_date)}</span>
          </div>
        </div>
        
        <div className="flex gap-1 ml-3 flex-shrink-0">
          {!showSynced && !isTemp && onSync && (
            <button
              onClick={() => onSync(expense)}
              disabled={isSyncing}
              className={`p-2 rounded-lg transition-colors ${
                isSyncing 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-orange-600 hover:bg-orange-50'
              }`}
              title={isSyncing ? "Синхронизация..." : "Синхронизировать с Google Sheets"}
            >
              {isSyncing ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <CloudArrowUpIcon className="w-5 h-5" />
              )}
            </button>
          )}
          <button
            onClick={() => onEdit(expense)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Редактировать"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCard;
